/**
 * ============================================================================
 *  retourSysteme.ts — Le bouton Retour d'Android ferme la feuille, pas la visite
 * ----------------------------------------------------------------------------
 *  Sans entrée d'historique, « Retour » quitterait la page (ou reviendrait à la
 *  scène d'avant, `UrlScene`) alors que le visiteur voulait seulement fermer la
 *  feuille. Chaque surcouche mobile EMPILE une entrée à l'ouverture ; un `popstate`
 *  ferme la plus haute.
 *
 *  Fermée par un autre geste (voile, glisser, ×), la surcouche dépile son entrée
 *  avec `history.back()` — et ce retour-là ne doit rien fermer de plus : il est
 *  compté dans `attendus`, que le prochain `popstate` consomme sans agir.
 *
 *  L'adresse ne change pas (`pushState` sans URL) : `UrlScene` n'a rien à suivre.
 * ==========================================================================*/

interface Entree {
  surRetour(): void;
}

const pile: Entree[] = [];
let attendus = 0;
let ecoute = false;

function surPopState() {
  if (attendus > 0) {
    attendus -= 1;
    return;
  }
  pile.pop()?.surRetour();
}

/**
 * Empile une entrée ; renvoie la fonction qui la dépile (à appeler à la fermeture).
 *
 * L'empilement est DIFFÉRÉ d'un tick. En développement, React (StrictMode) monte, démonte puis
 * remonte les effets aussitôt : empilé tout de suite, l'entrée serait dépilée par un
 * `history.back()` asynchrone qui arriverait APRÈS le second `pushState` et reculerait d'une entrée
 * de trop. Différé, le démontage simulé annule l'empilement avant qu'il n'ait eu lieu.
 */
export function empiler(surRetour: () => void): () => void {
  if (!ecoute) {
    window.addEventListener("popstate", surPopState);
    ecoute = true;
  }
  const entree: Entree = { surRetour };
  let empilee = false;
  const minuteur = window.setTimeout(() => {
    empilee = true;
    pile.push(entree);
    window.history.pushState(window.history.state, "");
  }, 0);
  return () => {
    if (!empilee) {
      window.clearTimeout(minuteur);
      return;
    }
    const rang = pile.indexOf(entree);
    // Déjà dépilée par le bouton Retour : l'historique est à jour.
    if (rang === -1) return;
    pile.splice(rang, 1);
    attendus += 1;
    window.history.back();
  };
}
